import { useMemo, lazy } from 'react'
import { useLocation } from 'react-router-dom'
import {
    LAYOUT_COLLAPSIBLE_SIDE,
    LAYOUT_STACKED_SIDE,
    LAYOUT_TOP_BAR_CLASSIC,
    LAYOUT_PLAIN_TOP_BAR,
    LAYOUT_FRAMELESS_SIDE,
    LAYOUT_CONTENT_OVERLAY,
    LAYOUT_BLANK,
} from '@/constants/theme.constant'
import type { CommonProps } from '@/@types/common'
import type { LazyExoticComponent } from 'react'
import type { LayoutType } from '@/@types/theme'
import PlainTopbar from './components/PlainTopbar'
import ChangePassword from '@/views/ChangePassword'
import ChangeOtp from '@/views/ChangeOtp/ChangeOtp'
import BirthdayAnniversaryWishes from '@/views/dashboard/BirthdayAnniversaryWishes'
import Consent from '@/views/Consent'
import ChatBot from "react-chatbotify";
import AiChatWidget from '@/components/shared/AiChatWidget'

type Layouts = Record<
    string,
    LazyExoticComponent<<T extends CommonProps>(props: T) => JSX.Element>
>

interface PostLoginLayoutProps extends CommonProps {
    layoutType: LayoutType
}

const layouts: Layouts = {
    [LAYOUT_COLLAPSIBLE_SIDE]: lazy(
        () => import('./components/CollapsibleSide'),
    ),
    [LAYOUT_STACKED_SIDE]: lazy(() => import('./components/StackedSide')),
    [LAYOUT_TOP_BAR_CLASSIC]: lazy(() => import('./components/TopBarClassic')),
    [LAYOUT_PLAIN_TOP_BAR]: lazy(() => import('./components/PlainTopbar')),
    [LAYOUT_FRAMELESS_SIDE]: lazy(() => import('./components/FrameLessSide')),
    [LAYOUT_CONTENT_OVERLAY]: lazy(() => import('./components/ContentOverlay')),
    [LAYOUT_BLANK]: lazy(() => import('./components/Blank')),
}

const PostLoginLayout = ({ layoutType, children }: PostLoginLayoutProps) => {
    const location = useLocation()
    const pathname = `/portal${location.pathname}`
    const AppLayout = useMemo(() => {
        if (!layouts[layoutType]) {
            return layouts[Object.keys(layouts)[0]]
        }

        return layouts[layoutType]
    }, [layoutType])

    // Chatbot
    const settings = {
        header: {
            showAvatar: false,
            avatar: "/portal/img/others/message-icon-3.png",
            title: "AI Assistant",
        },
        chatButton : {
            icon: "/portal/img/others/chatbot-icon.png"
        }
    }

    // Chatbot styles here
    const styles = {
        headerStyle: {
            backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: '#ffffff',
            padding: '10px',
        },
        chatWindowStyle: {
            backgroundColor: '#f7f9fc',
        },
        botBubbleStyle: {
            textAlign: "left" as const, // ✅ literal type
            color: "#334155",
            borderRadius: "12px 12px 12px 0",
            backgroundColor: "#e5e7eb",
            padding: "",
        },
        "userBubbleStyle": {
            // "backgroundColor": "#3b82f6",
            "backgroundImage": "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "color": "#ffffff",
            "borderRadius": "8px 8px 0 8px"
        },
        "chatInputContainerStyle": {
            "backgroundColor": "#e5e7eb"
        },
        "chatInputAreaStyle": {
            "minHeight": 0,
            "border": 0,
            "padding": "8px 15px",
            "backgroundColor": "white",
            "color": "#334155",
            "fontSize": "14px",
            "outlineColor": "transparent"
        },
        "sendButtonStyle": {
            "display": "none"
        },
        "chatHistoryButtonStyle": {
            "color": "#6b7280",
            "backgroundColor": "white",
            "border": "1px solid #e5e7eb"
        },
        "chatHistoryButtonHoveredStyle": {
            "color": "#3b82f6",
            "backgroundColor": "white",
            "border": "1px solid #3b82f6"
        },
        "tooltipStyle": {
            "padding": "8px 12px",
            "borderRadius": "15px",
            "color": "red"
        },
        "botOptionStyle": {
            "color": "#6b7280",
            "backgroundColor": "white",
            "border": "1px solid #e5e7eb"
        },
        "botOptionHoveredStyle": {
            "color": "#3b82f6",
            "backgroundColor": "transparent",
            "border": "1px solid #3b82f6",
            "boxShadow": "none"
        },
        "botCheckboxRowStyle": {
            "color": "#6b7280",
            "backgroundColor": "white",
            "border": "1px solid #e5e7eb",
            "borderRadius": "6px",
            "boxShadow": "none"
        },
        "botCheckboxNextStyle": {
            "color": "#6b7280",
            "backgroundColor": "white",
            "border": "1px solid #e5e7eb",
            "borderRadius": "6px",
            "boxShadow": "none"
        },
        "botCheckMarkSelectedStyle": {
            "backgroundColor": "#3b82f6"
        },
        "loadingSpinnerStyle": {
            "borderTop": "4px solid #3b82f6"
        }
    };

    const isConsentPath = pathname === '/portal/consent' || pathname.startsWith('/portal/consent/')

    return (
    <>
    { pathname === '/portal/change-password' || pathname === '/portal/change-otp'  || pathname === '/portal/dashboard/hrdashboard/birthdaywish' || isConsentPath ?
        <>
            <PlainTopbar hideUserProfile={isConsentPath} />
            { pathname === '/portal/change-password' ? <ChangePassword/> : null}
            { pathname === '/portal/change-otp' ? <ChangeOtp/> : null}
            { pathname === '/portal/dashboard/hrdashboard/birthdaywish' ? <BirthdayAnniversaryWishes/> : null}
            { isConsentPath ? <Consent/> : null}
        </>
        :  <AppLayout>
                {children}
                {/* Floating AI assistant on every post-login page (hidden on /mybot) */}
                <AiChatWidget />
                {/* <div className='chatbotify'>
                    <ChatBot styles={styles} settings={settings}/>
                </div> */}
            </AppLayout>
    }
    </>
    )
}

export default PostLoginLayout
